import React from "react";
import { useApolloClient } from "@apollo/client";
import { useStoreState, Session } from "ezStore";
import { intl } from "LocaleWrapper";

import { Checkbox, Table } from "@cofano/cck";
import { Box } from "@mui/material";

import { TableId, useBackendTable } from "components/table";

import { DossierLink, ProductLink } from "utils/LinkUtils";
import { getCargoBlDate } from "utils/cargoActionUtils";
import { FormattedDate } from "utils/DateUtils";

import StatusChanger from "view/statuses/components/changer/StatusChanger";

import { ChildPropsFromPagination } from "types/commons";
import { GetPreviousCargoesDocument, GetPreviousCargoesQuery } from "__generated__/view/cargos/graphql/cargo.queries.generated";
import { SortDirection, TankConfiguration, TankPosition, StatusType, TankNumberingScheme } from "__generated__/enums";
import { TankFragment } from "__generated__/view/ships/graphql/ship.fragments.generated";

export interface PreviousCargoesProps {
	ship: {
		id: number;
		tanks: TankFragment[];
		tankProperties?: {
			tankConfiguration?: TankConfiguration | null;
			tankNumberingScheme?: TankNumberingScheme | null;
		} | null;
	}
	setSelectedTanks?: React.Dispatch<React.SetStateAction<number[]>>;
	selectedTanks?: number[];
	filterOnCharterId?: number;
}

type CargoFromQuery = ChildPropsFromPagination<GetPreviousCargoesQuery, "previousCargoes">;

const LastShippedProductsTable = ({ ship, setSelectedTanks, selectedTanks, filterOnCharterId }: PreviousCargoesProps) => {

	const session = useStoreState(state => state.session);
	const client = useApolloClient();

	const handleCheckmarkChange = (tankId: number, checked: boolean) => {
		if (!setSelectedTanks) {
			return;
		}

		setSelectedTanks(ids => {
			return checked
				? Array.from(new Set([ ...ids, tankId ]))
				: ids.filter(x => x !== tankId);
		});
	};

	const { tableProps, refetch } = useBackendTable({
		id: TableId.PreviousCargoes,
		defaultSize: "COMPACT",
		defaultPageSize: 10,
		disableParams: true,
		onChange: async(pagination, sort, _filter, callback) => {
			try {
				const result = await client.query({
					query: GetPreviousCargoesDocument,
					variables: {
						pagination: {
							sortList: sort ?? [ {
								field: "firstLoadingAction.start",
								direction: SortDirection.Desc,
							} ],
							...pagination,
						},
						shipId: ship.id,
						charterId: filterOnCharterId,
					},
				});

				callback({
					content: result.data.previousCargoes.content,
					totalResults: result.data.previousCargoes.totalElements || 0,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	const generateTankColumns = () => {
		return ship.tanks
			.sort((a, b) => b.positionNumber - a.positionNumber)
			.map((tank, index) => {
				const isChecked = selectedTanks?.includes(tank.id) ?? false;

				const tankSuffix = ship.tankProperties?.tankConfiguration == TankConfiguration.PortStarboardTanks
					? intl.formatMessage({
						id: tank.position == TankPosition.Port
							? "SHIP_TANKS_PORT_SUFFIX"
							: "SHIP_TANKS_STARBOARD_SUFFIX",
					}) : "";

				return {
					id: "tank" + index,
					title: (
						<div style={{ alignItems: "center", display: "flex", justifyContent: "center", flexDirection: "column", width: "42px" }}>
							{setSelectedTanks && selectedTanks && (
								<div style={{ display: "flex", width: "14px", marginBottom: "12px" }}>
									<Checkbox
										size="small"
										checked={isChecked}
										value={undefined}
										onChange={(e) => handleCheckmarkChange(tank.id, e.target.checked)}
									/>
								</div>
							)}

							<div style={{ textAlign: "center" }}>
								{tank.positionNumber}{tankSuffix}
							</div>
						</div>
					),
					render: (cargo: CargoFromQuery) => {
						const appliesToTank = cargo.tanks?.map(x => x.id).includes(tank.id) || false;

						return <span style={{ display: "flex", justifyContent: "center", width: 24, height: 24, marginLeft: 9, alignItems: "center" }}>
							{appliesToTank ? `${tank.positionNumber}${tankSuffix}` : ""}
						</span>;
					},
					width: 50,
					disableFlexGrow: true,
				};
			});
	};

	return (
		<Box
			sx={{
				".cck-table": {
					".footer-settings": {
						"button": {
							display: "none",
						},
					},
				},
			}}>
			<div id="previousCargoesContainer" />

			<Table
				{...tableProps}
				key={selectedTanks ? selectedTanks.join(",") : "none"}
				columns={[ {
					id: "product",
					title: "",
					render: (cargo: CargoFromQuery) => (
						<CargoColumn
							cargo={cargo}
							refetch={refetch}
							session={session}
						/>
					),
					width: 330,
					disableFlexGrow: true,
				},
				...generateTankColumns(),
				]}
				container={document.getElementById("previousCargoesContainer") || undefined}
				columnResizingEnabled={false}
			/>
		</Box>
	);
};

const CargoColumn = ({ cargo, refetch, session }: { cargo: CargoFromQuery, refetch: () => void, session: Session }) => {
	const blDate = getCargoBlDate(cargo.actions ?? []);

	return (
		<Box sx={{ alignItems: "center", display: "flex", pl: "4px", pr: "4px" }}>
			<Box sx={{ display: "flex", flexDirection: "column" }}>
				<ProductLink product={cargo.product} customName={cargo.product?.previousCargoDisplayName} />
				<DossierLink charter={cargo.charter} />
			</Box>

			<Box sx={{ display: "flex", flexDirection: "column", alignItems: "end", justifyContent: "flexEnd", flexGrow: 1 }}>
				<StatusChanger
					session={session}
					entity={cargo}
					statusType={StatusType.Cargo}
					refetch={refetch}
				/>
				{blDate ? (
					<FormattedDate date={blDate} format="ll" />
				) : (
					<FormattedDate date={cargo.firstLoadingAction?.start} format="ll" />
				)}
			</Box>
		</Box>
	);
};

export default LastShippedProductsTable;
